import {
  MarketingCampaign,
  MarketingCampaignStep,
  Practice,
  RecurrencePattern,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  MarketingCampaignStepConversionType,
  MarketingCampaignType,
  TemplateScope,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  FactTables,
  reactivationNonHealthFundPatientsCampaignReportTemplate,
} from '@principle-theorem/reporting';
import {
  asReffable,
  CustomRecurrenceFrequency,
  DayOfWeek,
  DocumentReference,
  Frequency,
  Region,
} from '@principle-theorem/shared';
import { IMarketingCampaignTemplate } from './marketing-campaign-template';
import { MarketingCampaignTemplateFactory } from './marketing-campaign-template-factory';
import {
  toLineBreak,
  toLinkContent,
  toParagraphContent,
} from '@principle-theorem/editor';
import { CampaignTemplateHelpers } from './campaign-template-helpers';

export function getReactivationPatientsCampaignTemplate(
  practiceRef: DocumentReference<IPractice>
): IMarketingCampaignTemplate {
  return {
    name: 'Reactivation Patients (12-24 Months No Visit)',
    description: `A campaign targeting patients who haven't visited in 12-24 months, encouraging them to book an appointment and maintain their oral health.`,
    regions: [Region.NewZealand],
    campaign: MarketingCampaign.init({
      brandRef: Practice.brandRef(asReffable(practiceRef)),
      practiceRef,
      name: 'Reactivation Patients (12-24 Months No Visit)',
      type: MarketingCampaignType.GeneralNotice,
      scope: TemplateScope.Patient,
      maximumEntriesPerRun: 100,
      entryResetRules: {
        multipleEntry: true,
        reentryScope: TemplateScope.Patient,
        maxEntryCount: undefined,
        frequencyCount: 4,
        frequencyType: CustomRecurrenceFrequency.Monthly,
      },
      recurrencePattern: RecurrencePattern.init({
        customFrequencyType: CustomRecurrenceFrequency.Weekly,
        daysOfWeek: [DayOfWeek.Monday, DayOfWeek.Wednesday],
        frequencyType: Frequency.Custom,
      }),
      recurrenceRunTime: '08:04',
      conversionType: MarketingCampaignStepConversionType.AnyAppointmentBooked,
    }),
    report: reactivationNonHealthFundPatientsCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
    steps: [
      MarketingCampaignStep.init({
        name: 'Initial SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                `Hi {{ patientFirstName }}, it's been over a year since we saw you last! Now is the perfect time to book in at {{ practiceName }} for a routine check-up and clean, don’t let your oral health care fall by the wayside 🦷`,
                toLineBreak(),
                'Book here: {{ appointmentBookingLink }} or call {{ practicePhoneNumber }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Minutes,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Initial Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'We’d love to see you again! 🦷',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'It’s been over a year since your last visit, and we’d love to see you again! Completing treatment early when detected, and attending your routine check-up and cleans are vital to maintaining good oral health.',
                toLineBreak(),
                'Don’t let your oral health get put to the wayside. Booking in for your routine check-up and clean gives us the chance to:',
                '- Review any unfinished treatment',
                '- Detect and treat new issues early',
                '- Help you maintain long-term results',
                toLineBreak(),
                'Let’s make sure your smile gets the attention it deserves.',
                toLineBreak(),
                [
                  '🗓 Book here: ',
                  toLinkContent('{{ appointmentBookingLink }}'),
                ],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'We’d love to welcome you back,',
                'The {{ practiceName }} Team',
                toLineBreak(),
                CampaignTemplateHelpers.highlight('[INSERT PRACTICE LOGO]'),
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 3,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Second Reminder SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          `Hi {{ patientFirstName }}, it’s the team from {{ practiceName }}. It’s been over a year since we last saw you and we’d love to welcome you back. We understand that finding time to look after your oral health can be challenging, but we are here to help! Would you like me to find a time for you?`,
          {
            direction: TimingDirection.After,
            amount: 8,
            unit: TimingUnit.Days,
            time: '09:00',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Final SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, this is the team from {{ practiceName }}. It’s been a while since your last visit and we’d love to get you back on track.',
                'If you’d like to schedule your next appointment let us know so we can keep your smile in great shape 😊. Call on {{ practicePhoneNumber }} or text us back.',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 5,
            unit: TimingUnit.Days,
            time: '08:12',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Final Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'Let’s get your smile back on track 😁',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'We’d love to see you again! Completing treatment on time and keeping up with regular visits prevents discomfort and helps avoid extra work later.',
                'Even if you were only in for a routine check, regular visits mean we can catch changes early and save you from more complex care later on.',
                toLineBreak(),
                'Let’s make sure your smile gets the attention it deserves.',
                toLineBreak(),
                [
                  '🗓 Book here: ',
                  toLinkContent('{{ appointmentBookingLink }}'),
                ],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'Warmly,',
                'The {{ practiceName }} Team',
                toLineBreak(),
                CampaignTemplateHelpers.highlight('[INSERT PRACTICE LOGO]'),
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 9,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
    ],
  };
}
